<template>
  <div ref="root" class="relative flex">
    <button
      type="button"
      class="flex cursor-pointer rounded-full"
      @click="open = !open"
    >
      <Avatar :src="user?.avatarUrl" :size="36" />
    </button>

    <Transition name="popover-down">
      <Menu
        v-if="open"
        class="absolute right-0 top-[calc(100%+3px)] z-50 w-[280px] origin-top-right"
      >
        <div class="px-2.5 py-2">
          <div class="text-[13px] leading-[14px] text-gray-500">Account</div>

          <MenuAccountCard dense class="mt-2.5" />

          <MenuUsageCard
            dense
            class="mt-2.5"
            @action="onAction"
            @navigate="onNavigate"
          />
        </div>

        <Menu.Divider />

        <Menu.Item
          v-if="tier === 'pro'"
          :icon="MembershipThumbnail"
          @click="onMembership"
        >
          Dacr Membership card
        </Menu.Item>
        <Menu.Item :icon="GearIcon" @click="navigate('/settings')">
          Settings
        </Menu.Item>

        <Menu.Divider />

        <Menu.Item :icon="HelpIcon" @click="external('https://help.dacr.com')">
          Help Center
        </Menu.Item>
        <Menu.Item :icon="FileTextIcon" @click="external('/resources')">
          Resources
        </Menu.Item>

        <Menu.Divider />

        <Menu.Item :icon="SignOutIcon" @click="onSignOut"> Sign out </Menu.Item>
      </Menu>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Avatar, Menu } from '@dacr/library'
import { FileTextIcon, GearIcon, HelpIcon, SignOutIcon } from '@dacr/icons'
import { useRoute, useRouter } from 'vue-router'

import { useEventListener } from '@/shared/composables/useEventListener'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { openModal, Modals } from '@/shared/dialogs/modals'
import { useUserStore } from '@/shared/stores/user'
import { useUploadStore } from '@/features/upload/store'
import MenuAccountCard from './MenuAccountCard.vue'
import MenuUsageCard from './MenuUsageCard.vue'
import MembershipThumbnail from './MembershipThumbnail.vue'
import { useMenuModel } from './useMenuModel'

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const uploadStore = useUploadStore()
const { tier } = useMenuModel()

const user = computed(() => userStore.user)

const root = ref<HTMLElement>()
const open = ref(false)
const close = () => (open.value = false)

const navigate = (path: string) => {
  close()
  router.push(path)
}

const onMembership = () => {
  close()
  openDialog(Dialog.DacrMembership)
}

const external = (url: string) => {
  close()
  window.open(url, '_blank')
}

const onAction = () => {
  close()
  openModal(Modals.UpgradeToPro)
}

const onNavigate = (path: string) => navigate(path)

const onSignOut = () => {
  close()
  uploadStore.showUploadBar = false
  router.push('/signout')
}

useEventListener(window, 'click', (event) => {
  if (!root.value?.contains(event.target as Node)) close()
})

watch(() => route.fullPath, close)
</script>

<style scoped>
.account-menu-enter-active {
  transition:
    opacity 0.1s,
    transform 0.13s cubic-bezier(0.4, 0, 0.2, 1);
}
.account-menu-leave-active {
  transition:
    opacity 0.1s,
    transform 0.1s cubic-bezier(0.4, 0, 0.2, 1);
}
.account-menu-enter-from,
.account-menu-leave-to {
  opacity: 0;
  transform: translate(0, -8px);
}
</style>
